import Scene from "~/scene";
import MapInteractiveGeometry from "./interactiveGeometry";
import SceneMarker from "./scene/marker/marker";
import SceneLayerMarker from "./scene/layer/marker";
import SceneCoords from "./scene/common/coords";

interface iOptions {
	trackWithSpace: boolean;
	trackLength: number;
	interactiveGeometry?: MapInteractiveGeometry;
}


export default class InteractiveLineChart {
	_profile: any;

	_geometry: Array<SceneCoords> | Array<Array<SceneCoords>>;

	_opts: iOptions;

	_marker: SceneMarker;

	_distances: Array<{
		distance: number;
		ind: number;
		coord: SceneCoords;
	}>;

	_distanceLength: number;

	_show: boolean;

	_visibleMarker: boolean;

	_markLayer: SceneLayerMarker;

	/**
	 * constructor
	 * @param hProfile - vyskovy profil (LineChart)
	 * @param geometry - objekt nebo pole objektů instance SMap.Geometry
	 */
	constructor(hProfile, geometry, opts) {
		this._profile = hProfile;
		this._geometry = geometry;
		this._opts = {
			trackWithSpace: false,
			trackLength: 0,
			interactiveGeometry: null,
			...opts,
		};
		this._marker = null;
		this._distances = [];
		this._distanceLength = 0;
		this._show = false;
		this._visibleMarker = false;
		this._markLayer = Scene.createLayerMarker();

		if (this._opts.trackWithSpace) {
			const allDistances = [];
			let lastDist = null;
			let lastDistValue = 0;

			this._geometry.forEach((geom, ind) => {
				geom.forEach(coord => {
					const distance = lastDist ? coord.distance(lastDist) : 0;

					lastDistValue += distance;
					allDistances.push({
						distance: lastDistValue,
						ind,
						coord,
					});

					lastDist = coord;
				});
				lastDist = null;
			});

			let distanceLength = allDistances.length ? allDistances[allDistances.length - 1].distance : 0;

			if (this._opts.trackLength) {
				const ratio = this._opts.trackLength / distanceLength;

				distanceLength = this._opts.trackLength;
				allDistances.forEach(item => {
					item.distance = item.distance * ratio;
				});
			}

			this._distances = allDistances;
			this._distanceLength = distanceLength;
		}

		Scene.addLayer(this._markLayer);
		this._buildMarker();
		this._markLayer.addMarker(this._marker);
		this._setListeners();
	}

	/**
	 * destruktor
	 */
	destroy() {
		Scene.removeLayer(this._markLayer);

		this._marker.destroy();
		this._marker = null;

		this._profile.removeListener("line-chart-enter", this._enter, this);
		this._profile.removeListener("line-chart-move", this._move, this);
		this._profile.removeListener("line-chart-leave", this.hide, this);
	}

	/**
	 * je marker/cara na vyskovem profilu viditelny/a?
	 * @return {Boolean} - viditelnost
	 */
	visible() {
		return this._show;
	}

	/**
	 * zobrazeni markeru/ cary na vyskovem profilu
	 * @param frac - pozice v procentech
	 * @param hideMark - zobrazeni marku na geometrii
	 */
	show(frac: number, hideMark?: boolean) {
		if (!hideMark) {
			this._visibleMarker = true;
		}

		if (frac) {
			this.move(frac);
		}

		if (this._visibleMarker && !this._opts.interactiveGeometry) {
			this._markLayer.enable();
		}

		this._show = true;
	}

	/**
	 * skryti marku/cary na vyskovem profilu
	 */
	hide() {
		this._markLayer.disable();
		this._profile && this._profile.clear();
		this._show = false;
		this._visibleMarker = false;

		this._opts.interactiveGeometry?.hide();
	}

	/**
	 * posunuti marku/cary na vyskovem profilu
	 * @param frac - pozice v procentech <0; 1>
	 */
	move(frac: any) {
		if (!frac) {
			return;
		}
		const fraction = frac.data ? frac.data.fraction : frac;

		if (this._visibleMarker) {
			const coords = this._calcCursorPosition(fraction);

			if (this._opts.interactiveGeometry) {
				this._opts.interactiveGeometry.showOnCoords(coords);
			} else {
				this._marker.setCoords(coords);
			}
		}
		this._profile && this._profile.showAtPerc(fraction * 100);
	}

	/**
	 * vytvoreni markeru
	 */
	_buildMarker() {
		const opt = {
			anchor: { left: 12, bottom: 12 },
			url: "/img/route/marker.png",
			m3dData: {
				type: "lineChart",
			},
		};

		this._marker = Scene.createMarker(null, null, opt);
	}

	/**
	 * bnastaveni posluchacu na udalosti vyvolane vyskovym profilem
	 */
	_setListeners() {
		if (!this._profile) {
			return;
		}

		this._profile.addListener("line-chart-enter", this._enter, this);
		this._profile.addListener("line-chart-move", this._move, this);
		this._profile.addListener("line-chart-leave", this.hide, this);
	}

	_enter(data) {
		this._visibleMarker = true;

		const coords = this._calcCursorPosition(data.frac);

		if (this._opts.interactiveGeometry) {
			this._opts.interactiveGeometry.showOnCoords(coords);
		} else {
			this._marker.setCoords(coords);

			this._markLayer.enable();
		}

		this._show = true;
	}

	_move(data) {
		if (this._visibleMarker) {
			const coords = this._calcCursorPosition(data.frac);

			if (this._opts.interactiveGeometry) {
				this._opts.interactiveGeometry.showOnCoords(coords);
			} else {
				this._marker.setCoords(coords);
			}
		}
	}

	/**
	 * vypocet pozice markeru
	 * @param  frac - pozice v procentech
	 */
	_calcCursorPosition(frac: number): SceneCoords {
		if (this._opts.trackWithSpace) {
			const curDist = frac * this._distanceLength;
			let coords = this._distances[0].coord;

			for (let index = 0, max = this._distances.length - 1; index < max; index++) {
				const item = this._distances[index];
				const nextItem = this._distances[index + 1];

				if (curDist >= item.distance && curDist <= nextItem.distance) {
					const dist = nextItem.distance - item.distance;
					const ratioLeft = 1 - ((curDist - item.distance) / dist);
					const ratioRight = 1 - ((nextItem.distance - curDist) / dist);
					const leftCoords = item.coord.toWGS84();
					const rightCoords = nextItem.coord.toWGS84();
					const xAxis = (ratioLeft * leftCoords[0]) + (ratioRight * rightCoords[0]);
					const yAxis = (ratioLeft * leftCoords[1]) + (ratioRight * rightCoords[1]);

					coords = Scene.coordsFromWGS84(xAxis, yAxis);
					break;
				}
			}

			return coords;
		}

		const coords = [];
		const allGeoms = Array.isArray(this._geometry) ? this._geometry : [this._geometry];

		allGeoms.forEach(geom => {
			const geomCoord = geom.getCoords();

			if (geomCoord.length === 2) {
				const lastCoord = coords.length ? coords[coords.length - 1] : null;

				if (!lastCoord || !geomCoord[0].equals(lastCoord)) {
					coords.push(geomCoord[0]);
				}

				coords.push(geomCoord[1]);
			} else {
				geomCoord.forEach(coord => {
					coords.push(coord);
				});
			}
		});
		const distances = Mapy.Util.IP.countDistances(coords);
		// vezmeme celou delku trasy * frakce <0; 1>
		const distance = distances.length ? distances[distances.length - 1] * frac : 0;

		return Mapy.Util.IP.getCoordAtDistance(distance, coords, distances);
	}
}
